import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { CanvasElementId, CanvasElementType } from '@blaze-runtime/engine-domain';
import { FormContainerModelField } from '@blaze-shared/elements-domain';

import {
  FormChildrenContentOverrides,
  FormChildrenStyleOverrides,
} from '../elements/form-container/form-container.model';

@Injectable({ providedIn: 'root' })
export class ElementsMetaService {
  elementsMetaBaseMap = new Map<string, Observable<any>>();

  getOrCreateElementsFormMetaBase(
    formId: CanvasElementId,
    createBaseFn: () => Observable<any>,
  ): Observable<any> {
    return this.getOrCreateElementsMetaBase(`form:${formId}`, createBaseFn);
  }
  getOrCreateElementsConditionsMetaBase<T>(
    formId: CanvasElementId,
    elementId: CanvasElementId,
    elementType: CanvasElementType,
    conditionsHash: number,
    createBaseFn: () => Observable<T>,
  ): Observable<T> {
    const key = getElementConditionsMetaKey(formId, elementId, elementType, conditionsHash);
    if (!this.elementsMetaBaseMap.has(key)) {
      // the conditions have changed, remove old bases
      this.clearElementConditions(formId, elementId, elementType);
    }
    return this.getOrCreateElementsMetaBase(key, createBaseFn);
  }
  getOrCreateElementMetaBase(
    elementId: CanvasElementId,
    elementType: CanvasElementType,
    createBaseFn: () => Observable<any>,
  ): Observable<any> {
    const key = `meta:${elementId}:${elementType}`;
    if (!this.elementsMetaBaseMap.get(key)) {
      const existing = [...this.elementsMetaBaseMap.keys()].find((key) =>
        key.startsWith(`meta:${elementId}:`),
      );
      if (existing) {
        // the elementType has changed, remove old base
        this.elementsMetaBaseMap.delete(existing);
      }
    }
    return this.getOrCreateElementsMetaBase(`meta:${elementId}:${elementType}`, createBaseFn);
  }

  clearElementsFormMetaBase(formId: CanvasElementId): void {
    this.elementsMetaBaseMap.delete(`form:${formId}`);
    this.clearElementConditions(formId, undefined, undefined);
  }

  clearElementsMetaBase(elementId: CanvasElementId, elementType: CanvasElementType): void {
    this.elementsMetaBaseMap.delete(`meta:${elementId}:${elementType}`);
  }

  private clearElementConditions(
    formId: CanvasElementId,
    elementId: CanvasElementId | undefined,
    elementType: CanvasElementType | undefined,
  ): void {
    const elementKey = getElementConditionsMetaKey(formId, elementId, elementType);
    [...this.elementsMetaBaseMap.keys()]
      .filter((key) => key.startsWith(elementKey))
      .forEach((key) => this.elementsMetaBaseMap.delete(key));
  }

  private getOrCreateElementsMetaBase(
    key: string,
    createBaseFn: () => Observable<any>,
  ): Observable<any> {
    let base$ = this.elementsMetaBaseMap.get(key);
    if (!base$) {
      base$ = createBaseFn();
      this.elementsMetaBaseMap.set(key, base$);
    }
    return base$;
  }
}

function getElementConditionsMetaKey(
  formId: CanvasElementId,
  elementId: CanvasElementId | undefined,
  elementType: CanvasElementType | undefined,
  conditionsHash?: number,
): string {
  return (
    `conditions:${formId}:` +
    (elementId && elementType ? `${elementId}-${elementType}|` : '') +
    (conditionsHash ?? '')
  );
}

export interface MetaFormData {
  formId: string;
  formOptions:
    | undefined
    | {
        markRequiredFields: boolean | undefined;
        submitOnEnter: boolean | undefined;
        changeOnBlur: boolean | undefined;
      };
  formContentChildren: undefined | FormChildrenContentOverrides;
  formStyleChildren: undefined | FormChildrenStyleOverrides;
  modelFields: FormContainerModelField[] | undefined;
  fieldsLabelGen: number | undefined;
}
